import React, { useMemo, useState, useEffect } from "react";
import ProgressBar from "../../../Components/ProgressBar";
import styles from "./index.module.css";
import { useDispatch, useSelector } from "react-redux";
import { updateClubbedTripStatus } from "../action";
import ModalV2 from "../../../Components/ModalV2";
import CancelTrip from "../CancelTrip";
import {
  getProgressTemplateGeneric,
  TRIP_STATUS,
} from "../../../helpers/constants";
import EntitySection from "./EntitySection";
import DestinationSection from "./DestinationSection";
import ActionButtons from "./ActionButtons";
import ShowMore from "./ShowMore";
import { useTranslation } from "react-i18next";
import { IoWarningSharp } from "react-icons/io5";
import SwipeButton from "../../../Components/SwipeButton";
import Notes from "../Notes";
import { FaRegStickyNote } from "react-icons/fa";
import { IoIosMan } from "react-icons/io";

const GenericTransport = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { id: userId = "" } = useSelector((state) => state.login.user);

  const { type: taskType = "", clubbed_trip: clubbedTrips = [] } = useSelector(
    (state) => state.home.tripDetails
  );
  const {
    id: tripId,
    destination_details: destinationDetails = [],
    accepted_shifter_ids: acceptedShifters = [],
    assigned_shifters: assignedShifters = [],
    booking_request: {
      entity_name: entityName,
      priority = "",
      notes = "",
      no_of_shifters: shifterCount = 1,
    } = {},
    origin = {},
    type: tripType = "",
    status = "",
    shifter_type: shifterType = "",
  } = clubbedTrips?.[0] || {};

  const progressTemplate = useMemo(
    () => getProgressTemplateGeneric(destinationDetails),
    [destinationDetails]
  );

  const initialStep = useMemo(() => {
    const matchingStep = progressTemplate.findIndex(
      (step) => step?.prevStatus === status
    );
    return matchingStep >= 0 ? matchingStep + 1 : 1;
  }, [status, progressTemplate]);

  const [activeStep, setActiveStep] = useState(initialStep);
  const [isCancel, toggleCancel] = useState(false);
  const [isLoading, toggleLoading] = useState(false);
  const [showMore, toggleShowMore] = useState(false);
  const [data, setData] = useState(null);
  const [showNotes, toggleShowNotes] = useState(false);
  const [showAreaNotes, toggleShowAreaNotes] = useState(false);
  const [areaNotes, setAreaNotes] = useState("");

  const progress = useMemo(
    () =>
      progressTemplate.map((step, index) => ({
        ...step,
        active: index + 1 === activeStep,
      })),
    [activeStep, progressTemplate]
  );

  useEffect(() => {
    setActiveStep(initialStep);
  }, [initialStep]);

  const handleUpdate = () => {
    const body = {
      destination_id:
        progress?.[activeStep - 1]?.prevStatus === TRIP_STATUS.STARTED_SHIFTING
          ? destinationDetails?.[0]?.area?.id
          : "",
      notes: "",
    };
    toggleLoading(true);
    dispatch(
      updateClubbedTripStatus(
        tripId,
        progress?.[activeStep - 1]?.status,
        body,
        () => toggleLoading(false)
      )
    );
  };

  const handleMultiComplete = (id = "") => {
    const body = {
      destination_id: id,
      notes: "",
    };
    toggleLoading(true);
    dispatch(
      updateClubbedTripStatus(
        tripId,
        progress[activeStep - 1]?.status,
        body,
        () => toggleLoading(false)
      )
    );
  };

  const shouldRenderGlobalButton = !(
    destinationDetails?.length > 1 &&
    activeStep === 3 &&
    destinationDetails.some((item) => item?.status === TRIP_STATUS.BOOKED)
  );

  const handleHideMore = () => {
    setData(null);
    toggleShowMore(false);
  };

  const handleClickNotes = (notes) => {
    setAreaNotes(notes);
    toggleShowAreaNotes(true);
  };

  const handleCloseNotes = () => {
    setAreaNotes("");
    toggleShowAreaNotes(false);
  };

  return (
    <div className={styles["generic-transport"]}>
      <ProgressBar data={progress} activeStep={activeStep - 1} />
      <div className={styles["generic-transport__data"]}>
        <section className={styles["generic-transport__data-container"]}>
          {(priority === "emergency" || notes) && (
            <div
              className={styles["generic-transport__data-container__emergency"]}
            >
              {priority === "emergency" && (
                <IoWarningSharp size={18} color="#a71313" />
              )}
              {notes && (
                <div
                  className={styles["generic-transport__data-notes"]}
                  onClick={() => toggleShowNotes(true)}
                >
                  <FaRegStickyNote color="#237fff" size={16} />
                </div>
              )}
            </div>
          )}
          <label className={styles["generic-transport__mode"]}>{`${t(
            "Mode"
          )} : ${shifterType === "primary" ? t("Main") : t("Support")}`}</label>
          {assignedShifters?.length > 1 && (
            <div className={styles["generic-transport__data-container-title"]}>
              {assignedShifters
                ?.filter((item) => item.id !== userId)
                .map((shifter, key) => (
                  <label
                    key={key}
                    className={styles["generic-transport__multi-shifter"]}
                  >
                    <IoIosMan color="#2C4861" size={16} />
                    {shifter?.name}
                  </label>
                ))}
            </div>
          )}
          <EntitySection entityName={entityName} origin={origin} />

          <DestinationSection
            destinationDetails={destinationDetails}
            activeStep={activeStep}
            handleMultiComplete={handleMultiComplete}
            toggleShowMore={toggleShowMore}
            setData={setData}
            handleClickNotes={handleClickNotes}
            shifterType={shifterType}
          />

          {shifterType === "primary" && (
            <ActionButtons
              status={status}
              toggleCancel={() => toggleCancel(true)}
            />
          )}
        </section>
      </div>

      {shifterCount !== acceptedShifters?.length ? (
        <label className={styles["generic-transport__waiting"]}>
          {t("Waiting for others")}
        </label>
      ) : (
        shouldRenderGlobalButton &&
        shifterType === "primary" && (
          <div className={styles["generic-transport__button"]}>
            <SwipeButton
              onComplete={handleUpdate}
              loader={isLoading}
              label={t(progress?.[activeStep - 1]?.buttonName)}
            />
          </div>
        )
      )}
      {isCancel && (
        <ModalV2 onCancel={() => toggleCancel(false)} type="bottom">
          <CancelTrip
            onClose={() => toggleCancel(false)}
            type={"other"}
            tripId={tripId}
          />
        </ModalV2>
      )}
      {showMore && (
        <ModalV2 onCancel={handleHideMore} type="center">
          <ShowMore id={data} onCancel={handleHideMore} />
        </ModalV2>
      )}
      {showNotes && (
        <ModalV2 onCancel={() => toggleShowNotes(false)} type="center">
          <Notes notes={notes} onCancel={() => toggleShowNotes(false)} />
        </ModalV2>
      )}
      {showAreaNotes && (
        <ModalV2 onCancel={handleCloseNotes} type="center">
          <Notes notes={areaNotes} onCancel={handleCloseNotes} />
        </ModalV2>
      )}
    </div>
  );
};

export default GenericTransport;
